import { authHeaders } from "~/lib/auth-headers";
import { envConfig } from "~/lib/env-validation";

export interface CompanySuggestion {
	ico: string;
	name: string;
	city: string | null;
}

export interface CompanySearchResult {
	id: string;
	ico: string;
	name: string;
	legalForm: string | null;
	city: string | null;
	region: string | null;
	status: string;
	naceCode: string | null;
	naceDescription: string | null;
	employeeLabel: string | null;
}

export interface CompanySearchResponse {
	results: CompanySearchResult[];
	total: number;
	totalIsCapped: boolean;
	page: number;
	limit: number;
}

export class CompanySearchError extends Error {
	status: number | null;
	retryAfterSeconds: number | null;

	constructor(message: string, status: number | null, retryAfterSeconds: number | null = null) {
		super(message);
		this.name = "CompanySearchError";
		this.status = status;
		this.retryAfterSeconds = retryAfterSeconds;
	}
}

function errorMessage(status: number, retryAfterSeconds: number | null): string {
	if (status === 429) {
		return retryAfterSeconds
			? `Vyhledávání je dočasně omezené. Zkuste to znovu za ${retryAfterSeconds} s.`
			: "Vyhledávání je dočasně omezené. Zkuste to za chvíli znovu.";
	}
	if (status >= 500) return "Vyhledávání je dočasně nedostupné. Zkuste to prosím znovu.";
	return "Požadavek se nepodařilo zpracovat. Zkontrolujte hledaný výraz.";
}

async function searchFetch<T>(url: URL, signal?: AbortSignal): Promise<T> {
	let response: Response;
	try {
		response = await fetch(url.toString(), {
			headers: await authHeaders(),
			signal,
		});
	} catch (error) {
		if (error instanceof DOMException && error.name === "AbortError") throw error;
		throw new CompanySearchError(
			"Nepodařilo se spojit se serverem. Zkontrolujte připojení a zkuste to znovu.",
			null,
		);
	}

	if (!response.ok) {
		const retryAfter = Number(response.headers.get("Retry-After"));
		const retryAfterSeconds = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : null;
		throw new CompanySearchError(
			errorMessage(response.status, retryAfterSeconds),
			response.status,
			retryAfterSeconds,
		);
	}

	return response.json() as Promise<T>;
}

export async function fetchCompanySearch(
	q: string,
	page: number,
	limit: number,
	signal?: AbortSignal,
): Promise<CompanySearchResponse> {
	const url = new URL("/companies/search", envConfig.apiBaseUrl);
	url.searchParams.set("q", q);
	url.searchParams.set("page", String(page));
	url.searchParams.set("limit", String(limit));
	return searchFetch<CompanySearchResponse>(url, signal);
}

export async function fetchCompanyAutocomplete(
	q: string,
	signal?: AbortSignal,
): Promise<CompanySuggestion[]> {
	const url = new URL("/companies/autocomplete", envConfig.apiBaseUrl);
	url.searchParams.set("q", q);
	const response = await searchFetch<{ suggestions: CompanySuggestion[] }>(url, signal);
	return response.suggestions ?? [];
}

export function shouldRetryCompanySearch(failureCount: number, error: Error): boolean {
	if (failureCount >= 1) return false;
	if (!(error instanceof CompanySearchError)) return false;
	return error.status === null || error.status >= 500;
}
